import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { EntityId, removeEntity, setAllEntities, setEntity, withEntities } from '@ngrx/signals/entities';
import { Observable } from 'rxjs';
import { Sort, SortFn } from '../../shared/sort.util';
import { filterMultiple, filterBy as filterByFn } from '../../shared/filter.util';

type UpdaterFn<T> = (current: T) => Partial<T>;

type FilterOperator = '>' | '<' | '=' | '!=' | 'like';
export interface FilterConfig {
  operator?: FilterOperator;
  [key: string]: any;
}
export type FilterInput = FilterConfig | ((item: any) => boolean);

type SortOrder = 'asc' | 'desc';
export type SortConfig = { key: string; order?: SortOrder; type?: 'date' | 'string' | 'number' | 'boolean' };
export type SortInput = string | SortFn | SortConfig;
export type SortOption = SortInput | SortInput[] | undefined;

export interface EntityOptions {
  sortBy?: SortOption;
  filterBy?: FilterInput | FilterInput[];
}

function resolveFilterFn(filterBy: FilterInput | FilterInput[]) {
  const inputs = Array.isArray(filterBy) ? filterBy : [filterBy];
  const fns = inputs.map((f) => (typeof f === 'function' ? f : (({ operator, ...filters }) => filterByFn(filters, operator))(f)));
  return filterMultiple(...fns);
}

interface EntityConfig<T> {
  sortBy: SortInput | SortInput[];
  cacheTtl: number;
  updaterFn: (entity: T, injector?: Injector) => Partial<T>;
}

function resolveSortFns(sortBy: SortInput | SortInput[] | undefined): SortFn[] {
  const inputs: SortInput[] = sortBy === undefined ? [] : Array.isArray(sortBy) ? sortBy : [sortBy];
  return inputs.map((s) => {
    if (typeof s === 'string') return Sort.by(s);
    if (typeof s === 'function') return s;
    return s.type === 'date' ? Sort.byDate(s.key, s.order) : Sort.by(s.key, s.order, s.type ? { type: s.type } : undefined);
  });
}

/**
 * Reusable signal store feature that provides loading state, entity CRUD operations,
 * and reactive accessors for any entity type.
 *
 * Naming conventions:
 *  - `get*` — returns a computed signal, suitable for use in templates and other signals
 *  - `select*` — returns an Observable stream, suitable for components that cannot consume signals directly
 *
 * @param idKey - The property name on `T` that serves as the unique entity identifier.
 *
 * @example
 * export const MyStore = signalStore(
 *   withEntityState<MyEntity, 'MyId'>('MyId'),
 * );
 */
export function withEntityState<T extends { [P in K]: EntityId }, K extends string>(
  idKey: K,
  config: Partial<EntityConfig<T>> = {}
) {
  // Cast bypasses TypeScript's inability to resolve conditional types (EntityIdKey<T>) in generic contexts.
  // The constraint `T extends { [P in K]: EntityId }` already guarantees correctness at the call site.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const key = idKey as any;
  const cacheTtl = config.cacheTtl ?? 0;
  const updaterFn = config.updaterFn ?? null;

  return signalStoreFeature(
    withState({ isLoading: false, isLoaded: false, sortBy: resolveSortFns(config.sortBy), cachedAt: null as number | null }),
    withEntities<T>(),
    withMethods((store, injector = inject(Injector)) => ({
      /* ================================
       * Mutators
       * ================================ */

      /** Toggles the loading state — call before and after API requests. */
      setLoading: (isLoading: boolean) => patchState(store, { isLoading }),

      /** Replaces all entities in the store and marks it as loaded. */
      set: (entities: T[]) => {
        const mapped = updaterFn ? entities.map((e) => ({ ...e, ...updaterFn(e, injector) })) : entities;
        patchState(store, setAllEntities(mapped, { idKey: key }));
        patchState(store, { isLoaded: true, ...(cacheTtl > 0 ? { cachedAt: Date.now() } : {}) });
      },

      /** Returns true if a full-list fetch has been made within the configured TTL. Always false when `cacheTtl` is 0. */
      isCacheValid: () => {
        const cachedAt = store.cachedAt();
        return cacheTtl > 0 && cachedAt !== null && Date.now() - cachedAt < cacheTtl;
      },

      /** Invalidates the cache so the next fetch will hit the API. */
      clearCache: () => patchState(store, { cachedAt: null }),

      /**
       * Inserts an entity if it does not exist, or updates it if it does.
       * @TODO Replace with `upsertEntity` once the ngrx/signals version supports it.
       */
      upsert(entity: T) {
        const id = entity[idKey] as EntityId;
        const patched = updaterFn ? ({ ...entity, ...updaterFn(entity, injector) } as T) : entity;
        if (store.entityMap()[id]) {
          this.updateById(id, patched);
        } else {
          patchState(store, setEntity(patched, { idKey: key }));
        }
      },

      /**
       * Updates an entity by id. Accepts a partial object or an updater function.
       * Use the updater function form when the new value depends on the current state.=
       */
      updateById(id: EntityId, entityOrUpdater: Partial<T> | UpdaterFn<T>) {
        const current = store.entityMap()[id];
        if (!current) return;
        const patch = typeof entityOrUpdater === 'function' ? entityOrUpdater(current) : entityOrUpdater;
        patchState(store, setEntity({ ...current, ...patch, [idKey]: id } as T, { idKey: key }));
      },

      /** Removes an entity from the store by id. */
      removeEntity: (id: EntityId) => patchState(store, removeEntity(id)),

      /* ================================
       * Getters
       * ================================ */

      /**
       * Returns a computed signal for a single entity by id.=
       */
      getEntity(id: EntityId) {
        return computed(() => store.entityMap()[id]);
      },

      /**
       * Returns a computed signal of all entities, optionally overriding the store's default sort and filter.
       * @example // No options — uses the store's default sort
       * const lottos = store.getAll();
       * @example // Sort by a single property
       * const lottos = store.getAll({ sortBy: { key: 'Title', order: 'asc' } });
       * @example // Sort by multiple properties
       * const lottos = store.getAll({ sortBy: [{ key: 'Ordinal', order: 'asc' }, { key: 'Title', order: 'asc' }] });
       * @example // Filter with a predicate
       * const active = store.getAll({ filterBy: (item) => item.IsActive });
       * @example // Filter with a config object
       * const cheap = store.getAll({ filterBy: { PurchasePrice: 100, operator: '<' } });
       * @example // Combine sort and filter
       * const sorted = store.getAll({ sortBy: { key: 'PurchasePrice', order: 'desc' }, filterBy: (item) => item.IsActive });
       */
      getAll({ sortBy, filterBy }: EntityOptions = {}) {
        const resolvedSorts = sortBy !== undefined ? resolveSortFns(sortBy) : [];
        const defaultSort = store.sortBy() ?? [];
        const resolvedFilter = filterBy !== undefined ? resolveFilterFn(filterBy) : null;
        return computed(() => {
          const entities = resolvedFilter ? store.entities().filter(resolvedFilter) : store.entities();
          return entities
            .sort(
              // Apply default sort first
              Sort.multiple(...defaultSort)
            )
            .slice()
            .sort(Sort.multiple(...resolvedSorts));
        });
      },

      /**
       * Returns an Observable stream of a single entity by id.=
       */
      selectEntity(id: EntityId): Observable<T | undefined> {
        return toObservable(this.getEntity(id), { injector });
      },

      /**
       * Returns an Observable stream of all entities, optionally overriding the store's default sort and filter.
       * @example // No options — uses the store's default sort
       * store.selectAll().subscribe(lottos => ...)
       * @example // Sort by a single property
       * store.selectAll({ sortBy: { key: 'Title', order: 'asc' } }).subscribe(lottos => ...)
       * @example // Sort by multiple properties
       * store.selectAll({ sortBy: [{ key: 'Ordinal', order: 'asc' }, { key: 'Title', order: 'asc' }] }).subscribe(lottos => ...)
       * @example // Filter with a predicate
       * store.selectAll({ filterBy: (item) => item.IsActive }).subscribe(active => ...)
       * @example // Filter with a config object
       * store.selectAll({ filterBy: { PurchasePrice: 100, operator: '<' } }).subscribe(cheap => ...)
       * @example // Combine sort and filter
       * store.selectAll({ sortBy: { key: 'PurchasePrice', order: 'desc' }, filterBy: (item) => item.IsActive }).subscribe(sorted => ...)
       */
      selectAll(options: EntityOptions = {}): Observable<T[]> {
        return toObservable(this.getAll(options), { injector });
      },

      /**
       * Returns an Observable stream of the loading state.
       */
      selectLoading(): Observable<boolean> {
        return toObservable(store.isLoading, { injector });
      },
    }))
  );
}
